import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import Manifesto from "../components/Manifesto";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";
import { ArrowRight } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.products({ featured: true }).then(setFeatured).catch(() => {});
    api.categories().then(setCategories).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page" className="pt-16">
      <Hero />
      <Marquee />

      {/* CATEGORIES BENTO */}
      <section data-testid="categories-section" className="max-w-[1440px] mx-auto px-6 md:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
          <div>
            <div className="eyebrow mb-3">/ CATALOGUE — 07 DIVISIONS</div>
            <h2 className="font-display text-5xl md:text-7xl text-white tracking-tighter leading-[0.9]">
              Pick your poison.
            </h2>
          </div>
          <Link
            to="/shop"
            className="font-mono text-[11px] tracking-[0.24em] uppercase text-zinc-300 hover:text-white inline-flex items-center gap-2"
          >
            View all → <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {categories.map((c, i) => {
            const spans = [
              "md:col-span-8 md:row-span-2 aspect-[16/10]",
              "md:col-span-4 aspect-[4/5]",
              "md:col-span-4 aspect-[4/5]",
              "md:col-span-4 md:row-span-2 aspect-[4/5] md:aspect-auto",
              "md:col-span-4 aspect-[16/10]",
              "md:col-span-4 aspect-[16/10]",
              "md:col-span-12 aspect-[21/9]",
            ];
            return (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
                className={`pc-card group relative overflow-hidden border border-white/10 ${spans[i] || "md:col-span-4 aspect-[4/5]"}`}
              >
                <Link data-testid={`category-${c.slug}`} to={`/shop?category=${c.slug}`}>
                  <img src={c.image} alt={c.name} className="pc-img absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                  <div className="absolute top-4 left-4 font-mono text-[11px] tracking-[0.28em] uppercase text-white/70">
                    CH. {c.chapter_number}
                  </div>
                  <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                    <div>
                      <div className="font-display text-3xl md:text-5xl text-white uppercase tracking-tight leading-[0.9]">
                        {c.name}
                      </div>
                      <div className="font-mono text-[11px] text-zinc-300 mt-2 max-w-md">{c.tagline}</div>
                    </div>
                    <div className="h-10 w-10 border border-white flex items-center justify-center bg-black/40">
                      <ArrowRight className="w-4 h-4 text-white transition-transform group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      <Manifesto />

      {/* FEATURED */}
      <section data-testid="featured-section" className="max-w-[1440px] mx-auto px-6 md:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
          <div>
            <div className="eyebrow mb-3">/ FEATURED DROPS</div>
            <h2 className="font-display text-5xl md:text-7xl text-white tracking-tighter leading-[0.9]">
              Signed & numbered.
            </h2>
          </div>
          <Link to="/shop" className="font-mono text-[11px] tracking-[0.24em] uppercase text-zinc-300 hover:text-white">
            All products →
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featured.slice(0, 8).map((p, i) => (
            <ProductCard key={p.id} p={p} index={i} />
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section data-testid="cta-strip" className="relative py-24 md:py-32 border-y border-white/10 overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-40">
          <img
            src="https://images.unsplash.com/photo-1570984066691-54e56650764d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxtZXQlMjBwb2xpY2UlMjBoZWxpY29wdGVyJTIwbmlnaHR8ZW58MHx8fHwxNzg0OTk4OTYyfDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 text-center">
          <div className="eyebrow mb-4 text-center">/ ENROLLMENT</div>
          <h3 className="font-display text-white text-6xl md:text-8xl tracking-tighter leading-[0.9] mb-8">
            NO JUSTICE.<br />
            <span className="text-[color:var(--accent-red)]">NO PEACE.</span>
          </h3>
          <p className="text-zinc-300 max-w-lg mx-auto mb-10">
            Join over a thousand roleplayers writing UK street stories every night. Link your FiveM ID, pick a starter, hit the ends.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link
              to="/shop"
              className="btn-brutal h-12 px-8 border border-white text-white font-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center"
            >
              Enter Store
            </Link>
            <a
              href="https://discord.gg/brokenrp"
              target="_blank"
              rel="noreferrer"
              className="h-12 px-8 border border-white/20 text-zinc-300 hover:text-white hover:border-white font-mono text-[11px] tracking-[0.24em] uppercase inline-flex items-center"
            >
              Join Discord
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
